import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

// Shared gate for every rollups-v2 read call site (useTransactionsMonthsData,
// EmissionsContextProvider's vendor listener). Admin mode is excluded
// regardless of the flag: useCollectionDataListener's admin-mode branch only
// substitutes adminData for collections listed in adminDataCollections
// (helpers/firebase.js), which doesn't include the new rollups-v2
// collections -- left ungated, an admin viewing a canaried company would
// fall through to a plain listener scoped to the admin's OWN getAccountId(),
// not the company being viewed. rollups-v2 admin support is a real
// follow-up, not built here. One shared function so both call sites can
// never drift out of sync on this rule.
export const shouldUseRollupsV2Read = (rollupsV2ReadEnabled, viewMode) =>
  !!rollupsV2ReadEnabled && viewMode !== "admin";

// Phase 3 read-cutover: reshapes a rollups-v2 bucket-collection query result
// (one doc per bucket PER MONTH, map-based `subcategories` field -- see
// functions/utils/transactions/rollupWrites.js's own schema comment for why)
// back into the shape the legacy all-aggregated-transactions/
// matched-vendors-aggregated-transactions collections already produce (one
// doc per bucket holding ALL months, array-based `subcategoriesBreakdown`).
// Every existing consumer of that legacy shape --
// splitAggregatedTransactionsBreakdownSubcategories,
// formatVendors's extractAndSumVendorTransactions, everything downstream in
// helpers/contexts/emissions.js -- keeps working completely unchanged
// against either source. dayjs.utc(), not bare dayjs(): monthKey is a plain
// "YYYY-MM" string with no timezone of its own, so parsing it locally would
// resolve it to whatever month the viewer's browser timezone says.
//
// .add(1, "day") is what makes this actually match legacy, and it is NOT
// cosmetic: the legacy `date` field is not a bare UTC month start, it is
// `startOf("month").add(1, "day")` -- see correctDateForAggregation in
// functions/utils/transactions/transactionsAggregationFunctions.js.
// Confirmed against production: every legacy all-aggregated-transactions doc
// stores the 2nd (2026-01-02T00:00:00Z), never the 1st. That extra day is
// load-bearing for every consumer that reads the field back with a bare
// dayjs() -- chart-helpers' groupEmissionsByChartLabelsArray, explorer
// dashboardHelpers' filterEmissionsForYear, PurchaseVolumeSelectionInput.
// A bare UTC month start renders as the PREVIOUS month in any negative-UTC-
// offset timezone (January as the previous DECEMBER, i.e. the previous
// year), so dropping it silently shifted every transaction month back one
// on the dashboard for all 106 canaried companies.
//
// The rollups-v2 validator cannot catch this: it normalizes both sides to a
// UTC monthKey and compares per-month totals, which agree either way. The
// divergence only exists once the stored instant is formatted locally.
export const reshapeRollupsV2MonthDocsToLegacyShape = (rollupsV2MonthDocs = []) =>
  rollupsV2MonthDocs.map(({ monthKey, subcategories = {} }) => ({
    date: dayjs.utc(monthKey, "YYYY-MM").add(1, "day").toDate(),
    subcategoriesBreakdown: Object.entries(subcategories).map(
      ([subcategory, subcategoryData]) => ({
        subcategory,
        ...subcategoryData
      })
    )
  }));

// Shared O(n) grouping step for every per-entity grouper below (vendor,
// employee, event, office) -- one implementation so the four can't drift
// out of sync on it. Deliberately still Map-based, not a
// `{...acc, [key]: [...]}` reduce: that "plainly functional" alternative
// re-copies every distinct key already seen on every single doc, which is
// roughly O(docs * distinctEntities) -- real for a large canaried company
// with many vendors/employees, since this runs synchronously during
// render. Same idiom as rollupWrites.js's own deltaMap/subcategoryMap on
// the backend, and the same tradeoff already established (and reviewed)
// for vendors in PR #2037 before this file grew the employee/event/office
// groupers alongside it. The Map itself is locally-scoped, mutated only
// inside this one function call, and discarded before it returns -- every
// caller only ever observes the plain, immutable array this returns.
const groupDocsByEntityId = (docs, idField) => {
  const monthDocsByEntityId = new Map();

  docs.forEach((doc) => {
    const existingMonthDocs = monthDocsByEntityId.get(doc[idField]) || [];
    monthDocsByEntityId.set(doc[idField], [...existingMonthDocs, doc]);
  });

  return [...monthDocsByEntityId.entries()];
};

// Groups a flat rollups-v2-vendors collection query result (every vendor's
// every month, mixed together in one query) by vendorId into the per-vendor
// {companyId, aggregatedTransactions} shape matched-vendors-aggregated-
// transactions already produces -- `companyId` here means "the vendor's own
// v3-companies id", matching that legacy field's own (confusing but
// established) naming, since helpers/components/vendors.js's formatVendors
// already looks it up that way.
export const groupRollupsV2VendorDocsByVendor = (rollupsV2VendorDocs = []) =>
  groupDocsByEntityId(rollupsV2VendorDocs, "vendorId").map(
    ([vendorId, monthDocs]) => ({
      companyId: vendorId,
      aggregatedTransactions: reshapeRollupsV2MonthDocsToLegacyShape(monthDocs)
    })
  );

// Groups a flat rollups-v2-employees collection query result by employeeId
// into the per-employee {individualId, aggregatedTransactions} shape
// employees-aggregated-transactions already produces -- `individualId` is
// that legacy field's own name (the employee doc's own id, from the
// `employees` collection), which helpers/components/employees.js's
// useFindCurrentEmployeeEmissions already looks up by.
export const groupRollupsV2EmployeeDocsByEmployee = (
  rollupsV2EmployeeDocs = []
) =>
  groupDocsByEntityId(rollupsV2EmployeeDocs, "employeeId").map(
    ([employeeId, monthDocs]) => ({
      individualId: employeeId,
      aggregatedTransactions: reshapeRollupsV2MonthDocsToLegacyShape(monthDocs)
    })
  );

// Groups a flat rollups-v2-events collection query result by eventId into
// the per-event {eventId, aggregatedTransactions} shape
// events-aggregated-transactions already produces -- no rename needed here,
// unlike vendors/employees, since the legacy field is already `eventId`
// (matching @aclymatepackages/calcs/events' formatEventEmissionsTons lookup).
export const groupRollupsV2EventDocsByEvent = (rollupsV2EventDocs = []) =>
  groupDocsByEntityId(rollupsV2EventDocs, "eventId").map(
    ([eventId, monthDocs]) => ({
      eventId,
      aggregatedTransactions: reshapeRollupsV2MonthDocsToLegacyShape(monthDocs)
    })
  );

// Groups a flat rollups-v2-offices collection query result by officeId, then
// merges the reshaped result onto each already-fetched office object's
// `aggregatedTransactions` field, replacing the legacy embedded field.
// Offices are structurally different from vendors/employees/events: legacy
// `aggregatedTransactions` lives embedded ON the office doc itself (from the
// plain "offices" collection), not a parallel collection -- rollups-v2 moved
// the office bucket out into its own collection "for consistency with the
// other four bucket types" (see rollupWrites.js's own schema comment), so
// the front-end has to merge it back rather than just group-and-return. An
// office with no matching rollups-v2 bucket gets `aggregatedTransactions: []`,
// same as if legacy had no doc for it.
export const mergeRollupsV2OfficeDocsIntoOffices = (
  offices = [],
  rollupsV2OfficeDocs = []
) => {
  const monthDocsByOfficeId = new Map(
    groupDocsByEntityId(rollupsV2OfficeDocs, "officeId")
  );

  return offices.map((office) => ({
    ...office,
    aggregatedTransactions: reshapeRollupsV2MonthDocsToLegacyShape(
      monthDocsByOfficeId.get(office.id) || []
    )
  }));
};
